import type { CalendarEvent } from '../calendar/calendarAggregate';
import { fcDateToYmd } from '../calendar/calendarUrlState';

export const DASHBOARD_LIST_PAGE_SIZE = 10;

/** Viewport ~10 rows: without max-height the card grows and the sentinel never leaves view. */
export const DASHBOARD_LIST_VIEWPORT_CLASS =
  'cime-widget-list flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 max-h-80';

export function isPastHearing(event: CalendarEvent, now: Date): boolean {
  return event.kind === 'hearing' && new Date(event.start).getTime() < now.getTime();
}

export function selectOrdreDuJourEvents(
  events: CalendarEvent[],
  now: Date,
): CalendarEvent[] {
  const today = fcDateToYmd(now);
  const overdue: CalendarEvent[] = [];
  const upcoming: CalendarEvent[] = [];

  for (const event of events) {
    if (isPastHearing(event, now)) continue;
    const day = fcDateToYmd(new Date(event.start));
    if (event.kind !== 'hearing' && day < today) {
      overdue.push(event);
    } else if (day >= today) {
      upcoming.push(event);
    }
  }

  const byStart = (a: CalendarEvent, b: CalendarEvent) => a.start.localeCompare(b.start);
  overdue.sort(byStart);
  upcoming.sort(byStart);
  return [...overdue, ...upcoming];
}

export function revealSlice<T>(
  items: T[],
  visibleCount: number,
): { items: T[]; hasMore: boolean } {
  return {
    items: items.slice(0, visibleCount),
    hasMore: visibleCount < items.length,
  };
}

export function groupEventsByDay(
  events: CalendarEvent[],
): [string, CalendarEvent[]][] {
  const byDay = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const day = fcDateToYmd(new Date(event.start));
    const list = byDay.get(day) ?? [];
    list.push(event);
    byDay.set(day, list);
  }
  return [...byDay.entries()];
}

/**
 * Agenda du tableau de bord : tout ce qui est prévu à partir d'aujourd'hui, sans limite de
 * durée, du plus proche au plus lointain. Le défilement infini montre les semaines suivantes.
 */
export function selectUpcomingEvents(events: CalendarEvent[], now: Date): CalendarEvent[] {
  const todayStr = fcDateToYmd(now);
  return events
    .filter((e) => !isPastHearing(e, now) && fcDateToYmd(new Date(e.start)) >= todayStr)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

/** Dossiers récents : du plus récemment modifié au plus ancien. */
export function sortByRecentActivity<T extends { updated_at: string }>(dossiers: T[]): T[] {
  return [...dossiers].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}
